import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import FalconCloseButton from '../../../components/common/FalconCloseButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import SelectorCompaniaTarjeta from 'akasia-components/orden/seccion-pagos/SelectorCompaniaTarjeta';

const ModalConfirmarPagoManual = ({
    show,
    onHide,
    suscripcion,
    ordenPendiente,
    procesando,
    onConfirmar,
    estadoPago = { dSaldoRestante: 0, dSumaPagos: 0, bTodasOrdensPagadas: false }
}) => {
    const { token } = useToken();
    
    // Estados para el formulario de pago
    const [formasPago, setFormasPago] = useState([]);
    const [dataForm, setDataForm] = useState({
        dImportePagado: '',
        dSaldoInsoluto: 0,
        idCatFormaPago: '',
        dFechaPago: new Date().toISOString().split('T')[0],
        vReferencia: '',
        vArchivo: null,
        vNombreDocumento: '',
        vTipoDocumento: '',
        vTamano: 0,
        vJsonDetallesLinkPago: {}
    });

    // El saldo restante viene calculado correctamente desde el backend
    const saldoRestante = Number.parseFloat(estadoPago.dSaldoRestante || 0);

    /**
     * Carga formas de pago desde el backend
     */
    const cargarFormasPago = async () => {
        try {
            const response = await fetch('/api/Suscripciones/ObtenerFormasPago', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Las formas de pago vienen en table2
                const formasPagoData = data.table2 || [];
                // Filtrar para excluir forma de pago 22 (Stripe automático) y 99 (POR DEFINIR)
                const formasFiltradas = formasPagoData.filter(fp => 
                    fp.idCatFormaPago !== 22 && fp.idCatFormaPago !== 99
                );
                setFormasPago(formasFiltradas);
            }
        } catch (error) {
            console.error('Error al cargar formas de pago:', error);
        }
    };

    // Cargar formas de pago cuando se abre el modal
    useEffect(() => {
        if (show) {
            cargarFormasPago();
        }
    }, [show]);

    // Actualizar el formulario cuando cambia el saldo restante
    useEffect(() => {
        if (show && saldoRestante > 0) {
            setDataForm(prev => ({
                ...prev,
                dSaldoInsoluto: saldoRestante,
                dImportePagado: saldoRestante.toFixed(2) // Por defecto el total
            }));
        }
    }, [show, saldoRestante]);

    /**
     * Valida y envía el pago
     */
    const handleSubmit = () => {
        // Validaciones
        if (!dataForm.dImportePagado || Number.parseFloat(dataForm.dImportePagado) <= 0) {
            alert('El monto del pago debe ser mayor a 0');
            return;
        }

        if (Number.parseFloat(dataForm.dImportePagado) > saldoRestante) {
            alert(`El monto del pago no puede ser mayor al saldo restante: $${saldoRestante.toFixed(2)}`);
            return;
        }

        if (!dataForm.idCatFormaPago) {
            alert('Selecciona una forma de pago');
            return;
        }

        if (!dataForm.dFechaPago) {
            alert('Selecciona la fecha del pago');
            return;
        }

        if (!dataForm.vReferencia || dataForm.vReferencia.trim() === '') {
            alert('Ingresa el folio o referencia del pago');
            return;
        }

        // Llamar a onConfirmar con los datos del formulario
        onConfirmar(dataForm);
    };

    const montoNumerico = Number.parseFloat(dataForm.dImportePagado) || 0;
    const pagoCompleto = montoNumerico >= saldoRestante && montoNumerico > 0;

    // Solo requiere suscripción
    if (!suscripcion) return null;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="md"
            backdrop="static"
            aria-labelledby="modal-confirmar-pago-manual"
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    <FontAwesomeIcon icon="money-bill-wave" className="me-2 text-success" />
                    Registrar Pago Manual
                </Modal.Title>
                <FalconCloseButton className="btn btn-circle btn-sm transition-base p-0" onClick={onHide} />
            </Modal.Header>
            <Modal.Body>
                {/* Información de la suscripción */}
                <div className="bg-light rounded p-3 mb-3">
                    <div className="mb-2">
                        <strong>Suscripción:</strong> #{suscripcion.idSuscripcion}
                    </div>
                    {ordenPendiente && (
                        <div className="mb-2">
                            <strong>Orden:</strong> #{ordenPendiente.idOrden}
                        </div>
                    )}
                    <div className="mb-2">
                        <strong>Tipo de renovación:</strong>{' '}
                        <span className="text-capitalize">{suscripcion.vTipoRenovacion}</span>
                    </div>
                </div>

                {/* Saldo a pagar */}
                <Row>
                    <Col className="text-center mb-3">
                        <div className="fs-5 fw-semi-bold">Saldo a Pagar:</div>
                        <div className="fs-4 fw-semi-bold">
                            <span className="text-danger">
                                ${saldoRestante.toLocaleString("es-MX", {
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2
                                })}
                            </span> {ordenPendiente?.vMoneda || suscripcion.vMoneda || 'MXN'}
                        </div>
                        <hr />
                    </Col>
                </Row>

                {/* Monto del pago fijo (temporalmente no editable) */}
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Monto del Pago *</Form.Label>
                            <Form.Control
                                type="number"
                                size="md"
                                step="0.01"
                                min="0"
                                max={saldoRestante}
                                required
                                value={dataForm.dImportePagado}
                                disabled
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                </Row>

                {/*
                Saldo insoluto oculto temporalmente
                <Row>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label>Saldo Insoluto</Form.Label>
                            <Form.Control
                                type="number"
                                size="md"
                                disabled
                                value={dataForm.dSaldoInsoluto.toFixed(2)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                */}

                {/* Forma de pago */}
                <Form.Group className="mb-3">
                    <Form.Label>Forma de pago *</Form.Label>
                    <Form.Select
                        value={dataForm.idCatFormaPago}
                        onChange={(e) => {
                            const valor = e.target.value;
                            let cardType = '';
                            if (valor === "4") cardType = 'credit';
                            else if (valor === "18") cardType = 'debit';
                            
                            setDataForm({
                                ...dataForm,
                                idCatFormaPago: valor,
                                vJsonDetallesLinkPago: {
                                    ...dataForm.vJsonDetallesLinkPago,
                                    vCardType: cardType
                                }
                            });
                        }}
                    >
                        <option value="">Selecciona forma de pago</option>
                        {formasPago.map((fp) => (
                            <option key={fp.idCatFormaPago} value={fp.idCatFormaPago}>
                                {fp.vDescripcionFormaPago}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {/* Selector de compañía de tarjeta si es crédito o débito */}
                {(Number.parseInt(dataForm.idCatFormaPago) === 4 || Number.parseInt(dataForm.idCatFormaPago) === 18) && (
                    <SelectorCompaniaTarjeta
                        value={dataForm.vJsonDetallesLinkPago?.vCardCompany || ""}
                        onChange={(value) => {
                            const idFormaPago = Number.parseInt(dataForm.idCatFormaPago);
                            const cardType = idFormaPago === 4 ? 'credit' : 'debit';
                            
                            setDataForm({
                                ...dataForm,
                                vJsonDetallesLinkPago: {
                                    ...dataForm.vJsonDetallesLinkPago,
                                    vCardCompany: value,
                                    vCardType: dataForm.vJsonDetallesLinkPago?.vCardType || cardType
                                }
                            });
                        }}
                    />
                )}

                {/* Fecha del pago */}
                <Form.Group className="mb-3">
                    <Form.Label>Fecha del Pago *</Form.Label>
                    <Form.Control
                        onChange={(e) => setDataForm({ ...dataForm, dFechaPago: e.target.value })}
                        type="date"
                        size="md"
                        value={dataForm.dFechaPago}
                    />
                </Form.Group>

                {/* Referencia */}
                <Form.Group className="mb-3">
                    <Form.Label>Referencia *</Form.Label>
                    <Form.Control
                        onChange={(e) => setDataForm({ ...dataForm, vReferencia: e.target.value })}
                        type="text"
                        size="md"
                        required
                        value={dataForm.vReferencia}
                        placeholder="Ingresa folio o referencia"
                    />
                </Form.Group>

                {/* Comprobante de pago */}
                <Form.Group className="mb-3">
                    <Form.Label>Comprobante de pago</Form.Label>
                    <Form.Control
                        type="file"
                        size="sm"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setDataForm({
                                    ...dataForm,
                                    vArchivo: file,
                                    vNombreDocumento: file.name,
                                    vTipoDocumento: file.type,
                                    vTamano: file.size,
                                });
                            }
                        }}
                    />
                    <Form.Text className="text-muted">
                        Opcional. Adjunta comprobante de pago (imagen o PDF)
                    </Form.Text>
                </Form.Group>

                {/* Alertas según tipo de pago */}
                {pagoCompleto && (
                    <Alert variant="success" className="mt-3 mb-0">
                        <FontAwesomeIcon icon="check-circle" className="me-2" />
                        <strong>Pago completo:</strong> Se aplicará la renovación de la suscripción.
                    </Alert>
                )}

                {!pagoCompleto && montoNumerico > 0 && (
                    <Alert variant="info" className="mt-3 mb-0">
                        <FontAwesomeIcon icon="info-circle" className="me-2" />
                        <strong>Pago parcial:</strong> La suscripción se activará cuando se complete el pago total.
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} variant="secondary" disabled={procesando}>
                    Cancelar
                </Button>
                {procesando ? (
                    <Button variant="success" disabled>
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                        &nbsp;Procesando pago...
                    </Button>
                ) : (
                    <Button onClick={handleSubmit} variant="success">
                        <FontAwesomeIcon icon="check" className="me-1" />
                        Registrar Pago
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

ModalConfirmarPagoManual.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    suscripcion: PropTypes.object,
    ordenPendiente: PropTypes.object,
    procesando: PropTypes.bool.isRequired,
    onConfirmar: PropTypes.func.isRequired,
    estadoPago: PropTypes.shape({
        dSaldoRestante: PropTypes.number,
        dSumaPagos: PropTypes.number,
        bTodasOrdensPagadas: PropTypes.bool
    })
};

export default ModalConfirmarPagoManual;
